import { z } from 'zod';
import {
  isToolName,
  jsonValueSchema,
  nonEmptyJsonPointerSchema,
  toolCallSchema,
  toolChainSchema,
  type JsonValue,
  type ToolName,
} from '../flow';
import { isUnknownRecord as isRecord } from '../validation/unknown-record';

const idSchema = z
  .string()
  .min(1)
  .regex(/^[a-z][a-z0-9]*(?:[-_][a-z0-9]+)*$/, {
    message: 'id must be lowercase kebab-case or snake_case',
  });

export const agentDefinitionIdSchema = idSchema;
export const agentDefinitionVersionSchema = z.string().min(1);
export const agentScreenIdSchema = idSchema;
export const agentScreenVersionSchema = z.string().min(1);
export const agentScreenElementIdSchema = idSchema;

export const agentToolNameSchema = z
  .custom<ToolName>((value) => typeof value === 'string' && isToolName(value), {
    message: 'tool must be declared in the shared ToolCatalog',
  })
  .meta({ type: 'string' });

export const agentContextRequirementsSchema = z
  .object({
    required: z.array(z.string().min(1)),
    optional: z.array(z.string().min(1)).default([]),
    defaults: z.record(z.string(), jsonValueSchema).optional(),
  })
  .strict()
  .superRefine((context, ctx) => {
    addDuplicateIssues(context.required, ctx, ['required'], 'context key');
    addDuplicateIssues(context.optional, ctx, ['optional'], 'context key');

    const required = new Set(context.required);
    context.optional.forEach((key, index) => {
      if (required.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['optional', index],
          message: `context key "${key}" cannot be both required and optional`,
        });
      }
    });
  });

export type AgentContextRequirements = z.infer<typeof agentContextRequirementsSchema>;

export const agentScreenVisibilitySchema = z.discriminatedUnion('predicate', [
  z.object({ predicate: z.literal('always') }).strict(),
  z.object({ predicate: z.literal('exists') }).strict(),
  z.object({ predicate: z.literal('equals'), value: jsonValueSchema }).strict(),
]);

export type AgentScreenVisibility = z.infer<typeof agentScreenVisibilitySchema>;

/**
 * Agent-screen element ids for the interactive care-team booking card. Shared
 * so the server element definitions and the mobile host's slot write-back match
 * on one source of truth — renaming an element here updates both sides at once
 * rather than silently breaking the date/time lift-back.
 */
export const CARE_TEAM_BOOKING_ELEMENT_IDS = {
  select: 'care_team_select',
  confirm: 'care_team_confirm',
  booked: 'care_team_booked',
} as const;

export const agentRenderableSpecSchema = z
  .object({
    root: z.string().min(1),
    elements: z.record(z.string(), z.unknown()),
  })
  .strict()
  .superRefine(({ elements }, ctx) => {
    for (const [elementKey, element] of Object.entries(elements)) {
      if (!isRecord(element) || element.on === undefined) continue;
      if (!isRecord(element.on)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['elements', elementKey, 'on'],
          message: 'catalog element events must be an object of ToolCall bindings',
        });
        continue;
      }

      for (const [eventName, binding] of Object.entries(element.on)) {
        const result = Array.isArray(binding)
          ? toolChainSchema.safeParse(binding)
          : toolCallSchema.safeParse(binding);
        if (result.success) continue;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['elements', elementKey, 'on', eventName],
          message: 'catalog element event must contain a valid ToolCall or ToolChain',
        });
      }
    }
  });

export type AgentRenderableSpec = z.infer<typeof agentRenderableSpecSchema>;

export const agentScreenElementSchema = z
  .object({
    id: agentScreenElementIdSchema,
    statePath: nonEmptyJsonPointerSchema,
    visibility: agentScreenVisibilitySchema.default({ predicate: 'exists' }),
    accessibilityLabel: z.string().min(1),
    testId: z.string().min(1),
    spec: agentRenderableSpecSchema,
    // Element ids this element hides while it is visible. Lets a later card in
    // the journey (e.g. the weekly plan or an opt-in reminder) supersede an
    // earlier one (e.g. the insight) instead of stacking beside it. Absent or
    // empty means "coexists with everything".
    supersedes: z.array(agentScreenElementIdSchema).optional(),
  })
  .strict();

export type AgentScreenElement = z.infer<typeof agentScreenElementSchema>;

export const agentScreenSchema = z
  .object({
    id: agentScreenIdSchema,
    version: agentScreenVersionSchema,
    initialState: z.record(z.string(), jsonValueSchema).default({}),
    elements: z.array(agentScreenElementSchema).nonempty(),
  })
  .strict()
  .superRefine((screen, ctx) => {
    addDuplicateIssues(
      screen.elements.map((element) => element.id),
      ctx,
      ['elements'],
      'screen element id',
    );
    const reserved = findForbiddenScreenMetadata(screen);
    reserved.forEach((issue) => {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: issue.path,
        message: `AgentScreen must not contain host/provider metadata key "${issue.key}"`,
      });
    });
  });

export type AgentScreen = z.infer<typeof agentScreenSchema>;

export const agentPromptReferenceSchema = z
  .object({
    package: z.string().min(1),
    agent: z.string().min(1),
    version: z.string().min(1).optional(),
  })
  .strict();

export type AgentPromptReference = z.infer<typeof agentPromptReferenceSchema>;

export const agentProviderBindingSchema = z
  .object({
    provider: z.literal('elevenlabs'),
    binding: z.literal('server_resolved'),
  })
  .strict();

export type AgentProviderBinding = z.infer<typeof agentProviderBindingSchema>;

export const agentDefinitionPurposeSchema = z
  .object({
    kind: z.string().min(1),
    title: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
  })
  .strict();

export type AgentDefinitionPurpose = z.infer<typeof agentDefinitionPurposeSchema>;

export const agentDefinitionSchema = z
  .object({
    id: agentDefinitionIdSchema,
    version: agentDefinitionVersionSchema,
    purpose: agentDefinitionPurposeSchema,
    prompt: agentPromptReferenceSchema,
    providerBinding: agentProviderBindingSchema,
    context: agentContextRequirementsSchema,
    tools: z.array(agentToolNameSchema),
    screen: agentScreenSchema,
  })
  .strict()
  .superRefine((definition, ctx) => {
    addDuplicateIssues(definition.tools, ctx, ['tools'], 'tool');
  });

export type AgentDefinition = z.infer<typeof agentDefinitionSchema>;

export function isAgentDefinition(value: unknown): value is AgentDefinition {
  return agentDefinitionSchema.safeParse(value).success;
}

function addDuplicateIssues(
  values: readonly string[],
  ctx: z.RefinementCtx,
  pathPrefix: (string | number)[],
  label: string,
): void {
  const firstSeen = new Map<string, number>();

  values.forEach((value, index) => {
    const firstIndex = firstSeen.get(value);
    if (firstIndex === undefined) {
      firstSeen.set(value, index);
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...pathPrefix, index],
      message: `duplicate ${label} "${value}" also appears at index ${firstIndex}`,
    });
  });
}

type ForbiddenMetadataIssue = {
  key: string;
  path: (string | number)[];
};

const FORBIDDEN_SCREEN_METADATA_KEYS = new Set([
  'agentId',
  'agent_id',
  'auth',
  'authorization',
  'endpoint',
  'endpointUrl',
  'endpoint_url',
  'headers',
  'href',
  'implementation',
  'provider',
  'providerConfig',
  'providerMetadata',
  'provider_config',
  'provider_metadata',
  'provider_type',
  'providerType',
  'retry',
  'retryPolicy',
  'retry_policy',
  'route',
  'router',
  'transport',
  'url',
]);

function findForbiddenScreenMetadata(value: unknown): ForbiddenMetadataIssue[] {
  const issues: ForbiddenMetadataIssue[] = [];
  visitScreenValue(value, [], issues, new WeakSet<object>());
  return issues;
}

function visitScreenValue(
  value: unknown,
  path: (string | number)[],
  issues: ForbiddenMetadataIssue[],
  seen: WeakSet<object>,
): void {
  if (value === null || typeof value !== 'object') return;
  if (seen.has(value)) return;
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item, index) => visitScreenValue(item, [...path, index], issues, seen));
    return;
  }

  if (!isRecord(value)) return;
  Object.entries(value).forEach(([key, nested]) => {
    const nextPath = [...path, key];
    if (FORBIDDEN_SCREEN_METADATA_KEYS.has(key)) {
      issues.push({ key, path: nextPath });
    }
    visitScreenValue(nested, nextPath, issues, seen);
  });
}

export type AgentScreenState = Record<string, JsonValue>;
